/**
 * Surcharge display helpers shared by rate-line tables and the detail rail.
 * Mirrors the backend render rules: hide CNY, scale per-TEU by equipment size,
 * and keep per-B/L amounts out of the per-container totals.
 */

export const EXCLUDED_SURCHARGE_CURRENCIES = new Set(["CNY"]);

export type SurchargeCategoryKey = "ocean" | "local" | "other" | "inclusive";

export interface CategorySurchargeTotals {
  containerTotal: number;
  blTotal: number;
}

export function isRenderableSurcharge(s: { currency?: string | null }): boolean {
  return !EXCLUDED_SURCHARGE_CURRENCIES.has((s.currency ?? "").toUpperCase());
}

export function filterRenderableSurcharges<T extends { currency?: string | null }>(
  surcharges: T[],
): T[] {
  return surcharges.filter(isRenderableSurcharge);
}

/** Dedupe identical surcharge rows (e.g. repeated tables in email ingest). */
export function dedupeSurcharges<
  T extends {
    code?: string | null;
    amount: number;
    basis?: string | null;
    category?: string | null;
    currency?: string | null;
  },
>(surcharges: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const s of surcharges) {
    const key = [
      canonicalSurchargeCode(s.code ?? ""),
      s.amount,
      normSurchargeBasis(s.basis),
      surchargeCategoryKey(s.category),
      (s.currency ?? "").toUpperCase(),
    ].join("|");
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

/** Stable slot for stale-ingest cleanup (code + basis + category + currency). */
function staleSurchargeSlotKey<
  T extends {
    code?: string | null;
    basis?: string | null;
    category?: string | null;
    currency?: string | null;
  },
>(s: T): string {
  return [
    canonicalSurchargeCode(s.code ?? ""),
    normSurchargeBasis(s.basis),
    surchargeCategoryKey(s.category),
    (s.currency ?? "").toUpperCase(),
  ].join("|");
}

/**
 * One row per surcharge slot (first wins). Cleans up legacy ingest rows where
 * quoted email tables added a second THC/EFS with a stale amount. Distinct
 * bases for the same code (e.g. per-container THC + per-TEU THC after a
 * surcharge notice delta) are kept as separate slots.
 */
export function dedupeSurchargesByCode<
  T extends {
    code?: string | null;
    amount: number;
    basis?: string | null;
    category?: string | null;
    currency?: string | null;
  },
>(surcharges: T[]): T[] {
  const bySlot = new Map<string, T>();
  for (const s of surcharges) {
    const key = staleSurchargeSlotKey(s);
    if (!bySlot.has(key)) {
      bySlot.set(key, s);
    }
  }
  return [...bySlot.values()];
}

/** Surcharge codes touched by active surcharge notice overlays. */
export function overlaidSurchargeCodes(
  noticeOverlays?: Array<{ code?: string | null }> | null,
): Set<string> {
  const codes = new Set<string>();
  for (const o of noticeOverlays ?? []) {
    codes.add((o.code ?? "").toUpperCase());
  }
  return codes;
}

/**
 * When a notice overlay rewrites a code, drop duplicate rows in the same
 * code+basis slot (ingest echo). Never collapse different bases — a per-TEU
 * delta must stay beside a per-container base (mirrors backend overlay).
 */
export function dedupeOverlaidSurcharges<
  T extends { code?: string | null; basis?: string | null },
>(surcharges: T[], overlaidCodes: ReadonlySet<string>): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const s of surcharges) {
    const code = (s.code ?? "").toUpperCase();
    if (overlaidCodes.has(code)) {
      const slot = `${code}|${normSurchargeBasis(s.basis)}`;
      if (seen.has(slot)) continue;
      seen.add(slot);
    }
    out.push(s);
  }
  return out;
}

export interface SurchargesForLineDisplayOptions<
  T extends {
    code?: string | null;
    amount: number;
    basis?: string | null;
    category?: string | null;
    currency?: string | null;
  },
> {
  /** Notice-adjusted list when the API provides one (rate lookup / card lines). */
  surchargesOverlaid?: T[] | null;
  noticeOverlays?: Array<{ code?: string | null }> | null;
}

/** Options for notice-aware surcharge display on a rate line row. */
export function surchargeDisplayOptionsFromRateLine<
  T extends {
    code?: string | null;
    amount: number;
    basis?: string | null;
    category?: string | null;
    currency?: string | null;
  },
>(
  line: {
    surcharges: T[];
    surcharges_overlaid?: T[] | null;
    notice_overlays?: Array<{ code?: string | null }> | null;
  },
): SurchargesForLineDisplayOptions<T> {
  return {
    surchargesOverlaid: line.surcharges_overlaid,
    noticeOverlays: line.notice_overlays,
  };
}

/** Renderable surcharges with duplicate rows collapsed for totals and hero UI. */
export function surchargesForLineDisplay<
  T extends {
    code?: string | null;
    amount: number;
    basis?: string | null;
    category?: string | null;
    currency?: string | null;
  },
>(surcharges: T[], options?: SurchargesForLineDisplayOptions<T>): T[] {
  const overlaid = options?.surchargesOverlaid;
  const source =
    overlaid != null && overlaid.length > 0 ? overlaid : surcharges;
  let rows = dedupeSurchargesByCode(
    dedupeSurcharges(filterRenderableSurcharges(source)),
  );
  const overlaidCodes = overlaidSurchargeCodes(options?.noticeOverlays);
  if (overlaidCodes.size > 0) {
    rows = dedupeOverlaidSurcharges(rows, overlaidCodes);
  }
  return rows;
}

/** TEU conversion factor for a container size (mirrors EquipmentSize::teu_factor). */
export function teuFactorForSize(size?: string | null): number {
  if (!size) return 1;
  const match = String(size).match(/^\s*(\d+)/);
  if (!match) return 1;
  switch (match[1]) {
    case "20":
      return 1;
    case "40":
      return 2;
    case "45":
      return 2.25;
    default:
      return 1;
  }
}

export function normSurchargeBasis(basis?: string | null): string {
  return (basis ?? "").toLowerCase();
}

export function isBlBasis(basis?: string | null): boolean {
  return normSurchargeBasis(basis) === "bl";
}

export function effectiveSurchargeAmount(
  s: { basis?: string | null; amount: number },
  equipmentSize?: string | null,
): number {
  return normSurchargeBasis(s.basis) === "teu"
    ? s.amount * teuFactorForSize(equipmentSize)
    : s.amount;
}

export function surchargeCategoryKey(
  category?: string | null,
): SurchargeCategoryKey {
  if (category === "ocean" || category === "local" || category === "inclusive") {
    return category;
  }
  return "other";
}

/**
 * Per-category totals for the ocean/local columns.
 * Container + TEU amounts are the primary line; BL is kept separate.
 */
export function categorySurchargeTotals(
  surcharges: Array<{
    amount: number;
    currency?: string | null;
    basis?: string | null;
    category?: string | null;
    code?: string | null;
  }>,
  category: "ocean" | "local",
  lineCurrency: string,
  equipmentSize?: string | null,
  displayOptions?: SurchargesForLineDisplayOptions<(typeof surcharges)[number]>,
): CategorySurchargeTotals {
  const sameCcy = surchargesForLineDisplay(surcharges, displayOptions).filter(
    (s) =>
      s.category === category &&
      (s.currency ?? "").toUpperCase() === lineCurrency.toUpperCase(),
  );

  let containerTotal = 0;
  let blTotal = 0;
  for (const s of sameCcy) {
    const basis = normSurchargeBasis(s.basis);
    if (basis === "bl") {
      blTotal += s.amount;
    } else if (basis === "container" || basis === "teu") {
      containerTotal += effectiveSurchargeAmount(s, equipmentSize);
    }
  }
  return { containerTotal, blTotal };
}

/** Canonical surcharge code (mirrors backend `Surcharge::canonical_code`). */
export function canonicalSurchargeCode(code: string): string {
  const upper = code.trim().toUpperCase();
  const stripped = upper.startsWith("ACTUAL ") ? upper.slice(7).trim() : upper;
  return (stripped.split(" IN ")[0] ?? stripped).trim();
}

/** Whether a surcharge is terminal handling (THC/THD), including variants. */
export function isTerminalHandlingSurcharge(s: {
  code?: string | null;
}): boolean {
  const canonical = canonicalSurchargeCode(s.code ?? "");
  return canonical === "THC" || canonical === "THD";
}

export interface FrtPpSurcharge {
  amount: number;
  basis?: string | null;
  category?: string | null;
  currency?: string | null;
  code?: string | null;
}

/** Per-container surcharges that contribute to FRT++ for a line currency. */
export function countsTowardFrtPp(
  s: FrtPpSurcharge,
  lineCurrency: string,
): boolean {
  if (s.category === "inclusive") return false;
  if ((s.currency ?? "").toUpperCase() !== lineCurrency.toUpperCase()) {
    return false;
  }
  if (isTerminalHandlingSurcharge(s)) return false;
  const basis = normSurchargeBasis(s.basis);
  return basis === "container" || basis === "teu";
}

/** Same-currency per-container surcharges included in FRT++ (excl. THC/THD). */
export function frtPpSurcharges<T extends FrtPpSurcharge>(
  surcharges: T[],
  lineCurrency: string,
): T[] {
  return surchargesForLineDisplay(surcharges).filter((s) =>
    countsTowardFrtPp(s, lineCurrency),
  );
}

/** FRT++ = FRT + per-container surcharges excluding THC/THD and per-B/L. */
export function computeFrtPp(
  frt: number,
  currency: string,
  surcharges: FrtPpSurcharge[],
  equipmentSize?: string | null,
): number {
  const included = frtPpSurcharges(surcharges, currency);
  const surchargeTotal = included.reduce(
    (sum, s) => sum + effectiveSurchargeAmount(s, equipmentSize),
    0,
  );
  return frt + surchargeTotal;
}

/**
 * Display FRT++: notice overlay when present, then the API compound rate,
 * then a client-side recompute so the column still fills if the payload
 * omitted `frt_pp`.
 */
export function displayFrtPp(line: {
  frt: number;
  currency: string;
  equipment_size?: string | null;
  surcharges?: FrtPpSurcharge[] | null;
  frt_pp?: number | null;
  frt_pp_overlaid?: number | null;
}): number {
  return (
    line.frt_pp_overlaid ??
    line.frt_pp ??
    computeFrtPp(
      line.frt,
      line.currency,
      line.surcharges ?? [],
      line.equipment_size,
    )
  );
}
